import { ref, computed } from 'vue';

/**
 * System fields that the backend expects.
 * phone_number OR email must be mapped (at least one).
 */
export const SYSTEM_FIELDS = [
  { key: 'name', labelKey: 'CONTACT_IMPORT.MAPPING.FIELDS.NAME', required: false },
  {
    key: 'phone_number',
    labelKey: 'CONTACT_IMPORT.MAPPING.FIELDS.PHONE_NUMBER',
    required: false, // individually optional, but one of phone/email required
  },
  {
    key: 'email',
    labelKey: 'CONTACT_IMPORT.MAPPING.FIELDS.EMAIL',
    required: false,
  },
  { key: 'company', labelKey: 'CONTACT_IMPORT.MAPPING.FIELDS.COMPANY', required: false },
  { key: 'notes', labelKey: 'CONTACT_IMPORT.MAPPING.FIELDS.NOTES', required: false },
];

/**
 * Common aliases for auto-detection.
 * Keys are system field names, values are arrays of possible CSV header names.
 */
const COLUMN_ALIASES = {
  name: [
    'name', 'nome', 'nombre', 'full_name', 'fullname', 'full name',
    'nome completo', 'nome_completo', 'contato', 'contact', 'cliente',
    'client', 'razao social', 'razao_social',
  ],
  phone_number: [
    'phone_number', 'phonenumber', 'phone', 'telefone', 'tel', 'celular',
    'whatsapp', 'fone', 'mobile', 'numero', 'número', 'phone number',
    'numero_telefone', 'numero telefone', 'cell', 'cellular',
  ],
  email: [
    'email', 'e-mail', 'e_mail', 'correo', 'mail', 'email_address',
    'emailaddress', 'email address', 'endereco_email', 'endereco email',
  ],
  company: [
    'company', 'empresa', 'organização', 'organizacao', 'organization',
    'org', 'companhia', 'company_name', 'companyname', 'firma',
    'nome_empresa', 'nome empresa',
  ],
  notes: [
    'notes', 'notas', 'observações', 'observacoes', 'obs', 'comments',
    'comentários', 'comentarios', 'description', 'descrição', 'descricao',
    'nota', 'anotação', 'anotacao',
  ],
};

/**
 * Normalize a header string for comparison.
 * Strips accents, lowercases, replaces non-alphanumeric with underscore.
 */
function normalizeHeader(header) {
  return header
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Auto-detect which CSV headers map to which system fields.
 * Returns a mapping object { csvHeader: systemFieldKey }.
 */
export function autoDetectMapping(headers) {
  const mapping = {};
  const usedSystemFields = new Set();

  headers.forEach(header => {
    const normalized = normalizeHeader(header);
    const originalLower = header.trim().toLowerCase();

    for (const [systemField, aliases] of Object.entries(COLUMN_ALIASES)) {
      if (usedSystemFields.has(systemField)) continue;

      const match = aliases.some(alias => {
        const normalizedAlias = normalizeHeader(alias);
        return normalized === normalizedAlias || originalLower === alias;
      });

      if (match) {
        mapping[header] = systemField;
        usedSystemFields.add(systemField);
        break;
      }
    }
  });

  return mapping;
}

/**
 * Composable for managing CSV column mapping state.
 *
 * @param {import('vue').Ref<string[]>} headersRef - Reactive ref of CSV headers
 * @returns Mapping state and methods
 */
export function useCsvColumnMapping(headersRef) {
  const mapping = ref({});
  const autoDetectedFields = ref(new Set());

  /**
   * Run auto-detection on current headers and populate mapping.
   */
  function runAutoDetect() {
    const detected = autoDetectMapping(headersRef.value);
    mapping.value = { ...detected };
    autoDetectedFields.value = new Set(Object.keys(detected));
  }

  /**
   * Set mapping for a specific CSV header.
   */
  function setMapping(csvHeader, systemField) {
    const newMapping = { ...mapping.value };

    // If this system field was already mapped to another header, clear it
    if (systemField) {
      Object.entries(newMapping).forEach(([key, value]) => {
        if (value === systemField && key !== csvHeader) {
          delete newMapping[key];
        }
      });
    }

    if (systemField) {
      newMapping[csvHeader] = systemField;
    } else {
      delete newMapping[csvHeader];
    }

    mapping.value = newMapping;
  }

  /**
   * Clear mapping for a specific CSV header.
   */
  function clearMapping(csvHeader) {
    const newMapping = { ...mapping.value };
    delete newMapping[csvHeader];
    mapping.value = newMapping;
  }

  /**
   * Check if a system field is already mapped to some CSV header.
   */
  const mappedSystemFields = computed(() => {
    return new Set(Object.values(mapping.value));
  });

  /**
   * Validation: at least phone_number or email must be mapped.
   */
  const isValid = computed(() => {
    const mapped = mappedSystemFields.value;
    return mapped.has('phone_number') || mapped.has('email');
  });

  /**
   * Check if all mappable fields were auto-detected.
   */
  const allAutoDetected = computed(() => {
    return isValid.value && autoDetectedFields.value.size > 0;
  });

  /**
   * Check if a specific header was auto-detected.
   */
  function isAutoDetected(csvHeader) {
    return autoDetectedFields.value.has(csvHeader);
  }

  return {
    mapping,
    runAutoDetect,
    setMapping,
    clearMapping,
    mappedSystemFields,
    isValid,
    allAutoDetected,
    isAutoDetected,
  };
}
